import React, { useMemo, useRef, useState } from "react";
import { RCCAlert, RCCBadge, RCCButton, RCCDropDownItem, RCCGrid, RCCIcon, RCCInput, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import FiltersContainer from "src/components/FiltersContainer";
import Select from "src/components/Select";
import SidebarCard from "src/components/SidebarCard";
import { useTranslation } from "src/context/Translations/context";
import OptionsColumn from "src/components/DefaultColumnsList/Options";
import PageTitle from "src/components/PageTitle";
import { IRefSidebarCard } from "src/components/SidebarCard/interface";
import AppIcons from "src/components/AppIcons";
import CheckPermission from "src/components/CheckPermission";
import { PermissionEnum } from "src/context/Auth/interface";
import { simplePermissionType } from "src/components/Permissions/interface";
import useEffectFetchData from "src/hooks/useEffectFetchData";
import Loader from "src/components/Loader";
import { Card, SidebarCardBody } from "./styles";
import { IMatba001Form, IRefAnalysisModal } from "./interface";
import AnalysisContent from "./content";
import ModalAnalysisPanel from "./Components/ModalAnalysis";
import ModalRadarChart, { IRefModalRadarChart } from "./Components/modalRadarChart";

const AnalysisPanel: React.FC = () => {

	const { translate } = useTranslation();
	
	const [cards, setCards] = useState<IMatba001Form[]>([]);
	const [selectedAnalysis, setSelectedAnalysis] = useState<IMatba001Form | null>(null);
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<{ filter: string ,active:string}>({ filter: "",active:"T" });

	const refModalRadarChart = useRef<IRefModalRadarChart>(null);
	const refModalAnalysis = useRef<IRefAnalysisModal>(null);
	const refSidebarCard = useRef<IRefSidebarCard>(null);

	const { errorFetching, loading, loadData } = useEffectFetchData<IMatba001Form[]> (
		"Analysis/List", 
		 { 
			...filters
		 },
		 ({ data }) => {
			const dataFilterPermission = data.filter((x) => x.userPermission > Number(simplePermissionType.NO_PERMISSION));
			setCards(dataFilterPermission);
		 },
		 [filters]
	);

	const submitFilters = (data: any) => {
		setSelectedAnalysis(null);
		setFilters({ filter: data.filter, active:data.active.value });
	};

	const { 
		handleSubmit : handleSubmitFilters, 
		control: controlFilters, 
		reset: resetFilters,
		register: registerFilters,
		watch: watchFilters
	} = useForm();

	const callbackDeleteModel = (id: number) => {
		setCards((prevState) => prevState.filter(({ maa001Id }) => maa001Id !== id));
		if(selectedAnalysis?.maa001Id === id)
			setSelectedAnalysis(null);
	};
	
	const onClickCardFuncion = async (cardId: number) => {
		const element = cards.find((item) => item.maa001Id === cardId);
		if(!element){
			setSelectedAnalysis(null);
			return;
		}
		setSelectedAnalysis(element);
		refSidebarCard.current?.changeListVisibility();
	};

	const onCallbackAddModel = async () => {
		await loadData();
	};

	const onCallbackChangeModel = async (item: IMatba001Form) => {
		setSelectedAnalysis(item);
		await loadData();
	};

	const contentMemo = useMemo(() => (
		<>
			{selectedAnalysis ? (
				<AnalysisContent
					modelSelected={selectedAnalysis}
				/>
			): (
				<RCCAlert 
					title={translate("Atenção")}
					description={translate("Nenhuma análise selecionada")}
				/>
			)}
		</>
	), [selectedAnalysis]);

	return (
		<>
			<RCCGrid
				container
				item
				direction="column"
			>
		
				<PageTitle
					title={translate("Análises")}
					icon={() => <AppIcons name="analysis" />}
				/>

				<SidebarCard 
					ref={refSidebarCard}
					buttonsContent={
						<>
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
								<RCCTooltip
									text={translate("Nova análise")}
									color="purple"
								>
									<RCCButton
										icon={{name: "plus", color: "#FFF", size: 16}}
										color="purple"
										layout="isRounded"
										size="md"
										onClick={() => refModalAnalysis.current?.actionButtonAdd()}
									/>
								</RCCTooltip>
							</CheckPermission>
							<RCCTooltip
								text={translate("Filtros")}
								color={showFilters || filters.filter || filters.active !== "T" ? "blue" : "grey"}
							>
								<RCCButton
									icon={{name: "filter", color: "#FFF", size: 16}}
									color={showFilters || filters.filter || filters.active !== "T" ? "blue" : "grey"}
									layout="isRounded"
									size="md"
									onClick={() => setShowFilters((prevState) => !prevState)}
								/>
							</RCCTooltip>
						</>
					}
					filterContent={
						<>
							<FiltersContainer
								onSubmitFilters={handleSubmitFilters(submitFilters)}
								resetFilters={() => resetFilters({ filter: "", active: {value: "T", label: translate("Todos")}})}
								showFilters={showFilters}
							>
								<RCCGrid
									container
									item
								>
									<RCCGrid
										container 
										item
										xs={12}
									>
										<Select 
											control={controlFilters}
											name="active" 
											options={[
												{value: "S", label: translate("Sim")}, 
												{value: "N", label: translate("Não")},
												{value: "T", label: translate("Todos")}
											]} 
											label={translate("Ativos")}
											defaultValue={{value: "T", label: translate("Todos")}}
											submit="value and label"
										/>
									</RCCGrid>
									<RCCGrid
										container 
										item
										xs={12}
									>
										<RCCInput
											name="filter" label={translate("Pesquisar")}
											register={registerFilters}
											defaultValue={watchFilters("filter")}
										/>
									</RCCGrid>
								</RCCGrid>
							</FiltersContainer>
						</>
					}
					customCards={
						<>
							{errorFetching ? (
								<RCCAlert 
									color="red"
									title={translate("Atenção")}
									description={translate("Erro ao carregar dados")}
								/>
							): 
								<>
									{loading ? 
										<Loader /> : 
										<>
											{!cards || cards.length <= 0 ? (
												<RCCAlert
													description={translate("Não foram encontrados dados")}
												/>
											): (
												<>
													{cards.map((card: IMatba001Form) => 
														<Card 
															key={card.maa001Id} 														
															selected={card.maa001Id === selectedAnalysis?.maa001Id}
															color={card.maa001Ativo === "S"? "var(--green)": "var(--red)"}
														>
															<div className="card-head">
																<span className="icon-options">
																	<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
																		{
																			card.userPermission > Number(simplePermissionType.VIEW) &&
																		<OptionsColumn position="leftEnd">
																			<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
																				<RCCDropDownItem
																					selfEnclose
																					onClick={() => refModalAnalysis.current?.actionButtonChange(card, false)}
																				>
																					<RCCIcon name="pencil" />
																					{translate("Alterar")}
																				</RCCDropDownItem>
																			</CheckPermission>
																			<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
																				<RCCDropDownItem
																					selfEnclose
																					onClick={() => refModalAnalysis.current?.actionButtonDelete(card)}
																				>
																					<RCCIcon name="trash" />
																					{translate("Excluir")}
																				</RCCDropDownItem>
																			</CheckPermission>
																		</OptionsColumn>
																		}
																		{
																			card.userPermission === Number(simplePermissionType.VIEW) &&
																		<OptionsColumn position="leftEnd">
																			<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
																				<RCCDropDownItem
																					selfEnclose
																					onClick={() => refModalAnalysis.current?.actionButtonChange(card, true)}
																				>
																					<RCCIcon name="eye" />
																					{translate("Visualizar")}
																				</RCCDropDownItem>
																			</CheckPermission>
																		</OptionsColumn>
																		}
																	</CheckPermission>
																</span>
															</div>
															<SidebarCardBody onClick={(e) => {e.stopPropagation(); onClickCardFuncion(card.maa001Id);}} >
																<div className="card-body">
																	<span title={card.maa001Titulo}>{card.maa001Titulo}</span>
																</div>
																{
																	card.maa001Analisemodelo === "S" &&
																<RCCBadge color="grey"
																	styleInline="font-size: 0.83rem;"
																>
																	{translate("Modelo")}
																</RCCBadge>
																}
															</SidebarCardBody>
														</Card>
													)}
												</>
											)}	
										</>
									}
								</>
							}
						</>
					}
					content={contentMemo}
				/>
			</RCCGrid>


			<ModalAnalysisPanel 
				ref={refModalAnalysis} 
				onCallbackAdd={onCallbackAddModel}
				onCallbackChange={onCallbackChangeModel}
				onCallbackDelete={callbackDeleteModel}
			/>

			<ModalRadarChart ref={refModalRadarChart} />
		</>
	);

};
	
export default AnalysisPanel;
